import { Obu } from '_lib/api';
import { useAppDispatch } from '_store';
import { DenmSourceFilter } from '_store/denm/denmsTypes';
import { settingsSelector } from '_store/settings/selectors';
import settingsReducer from '_store/settings/settingsReducers';
import { useLocalization } from '@fluent/react';
import {
  Box,
  CardContent,
  FormControlLabel,
  Switch,
  Typography,
} from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { RoleGuard } from '../../items/RoleGuard';
import ObuForm from './ObuForm';
import styles from './styles';

const SettingsLayout: FC = ({}) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const { canManageSettings, blockedActionMode } = useRolePermissions();

  const settings = useSelector(settingsSelector);
  const [obuList, setObuList] = useState<Obu[]>(undefined);
  const blockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const handleObusChange = (updatedObus: Obu[]) => {
    setObuList(updatedObus);
    const obuPayload = updatedObus.reduce((acc, obu) => {
      if (obu.id && obu.name) {
        acc[obu.id] = obu.name;
      }
      return acc;
    }, {} as Record<string, string>);

    dispatch(
      settingsReducer.actions.updateSettings({
        storage: {
          cam: {
            obu: obuPayload,
          },
        },
      }),
    );
  };

  useEffect(() => {
    setObuList(settings.storage.cam.obu);
  }, [settings.storage.cam.obu]);

  const handleDenmDistributionChange =
    (sourceFilter: DenmSourceFilter | string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked;
      dispatch(
        settingsReducer.actions.updateSettings({
          distribution: {
            denm: {
              source: sourceFilter,
              value,
            },
          },
        }),
      );
    };

  const handleIvimDistributionChange =
    (source: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked;
      dispatch(
        settingsReducer.actions.updateSettings({
          distribution: {
            ivim: {
              source,
              value,
            },
          },
        }),
      );
    };

  const handleObuFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    dispatch(
      settingsReducer.actions.updateSettings({
        storage: {
          cam: {
            obuFilterEnabled: value,
          },
        },
      }),
    );
  };

  const handleTestObuToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    dispatch(
      settingsReducer.actions.updateSettings({
        storage: {
          cam: {
            simulatorToggle: value,
          },
        },
      }),
    );
  };

  return (
    <CardContent className={classes.cardContent}>
      <Box className={classes.box}>
        <Typography variant="caption" className={classes.heading}>
          {l10n.getString('forms-settings-denms-distribution')}
        </Typography>
      </Box>
      <Box className={classes.containerBox}>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.distribution.denm.emeras}
                  disabled={!canManageSettings}
                  onChange={handleDenmDistributionChange(DenmSourceFilter.EMERAS)}
                  name="distributionEmeras"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-distribution-emeras')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.distribution.denm.datexii}
                  disabled={!canManageSettings}
                  onChange={handleDenmDistributionChange(DenmSourceFilter.DATEXII)}
                  name="distributionDatexii"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-distribution-datexii')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
      </Box>
      <Box className={classes.box}>
        <Typography variant="caption" className={classes.heading}>
          {l10n.getString('forms-settings-denms-distribution-v2i')}
        </Typography>
      </Box>
      <Box className={classes.containerBox}>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.distribution.denm.connex}
                  disabled={!canManageSettings}
                  onChange={handleDenmDistributionChange(DenmSourceFilter.CONNEX)}
                  name="distributionConnex"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-distribution-connex')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
      </Box>
      <Box className={classes.box}>
        <Typography variant="caption" className={classes.heading}>
          {l10n.getString('forms-settings-distribution-amqp-i2i')}
        </Typography>
      </Box>
      <Box className={classes.containerBox}>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.distribution.denm.brokerTim}
                  disabled={!canManageSettings}
                  onChange={handleDenmDistributionChange('brokerTim')}
                  name="distributionDenmBrokerTim"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-distribution-broker-tim-denm')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.distribution.ivim.brokerTim}
                  disabled={!canManageSettings}
                  onChange={handleIvimDistributionChange('brokerTim')}
                  name="distributionIvimBrokerTim"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-distribution-broker-tim-ivim')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
      </Box>
      <Box className={classes.box}>
        <Typography variant="caption" className={classes.heading}>
          {l10n.getString('forms-settings-obu-configuration')}
        </Typography>
      </Box>
      <Box className={classes.containerBox}>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.storage.cam.simulatorToggle}
                  disabled={!canManageSettings}
                  onChange={handleTestObuToggleChange}
                  name="obuSimulatorToggle"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-toggle-obu-test')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
        <Box className={classes.box}>
          <RoleGuard
            allowed={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.storage.cam.obuFilterEnabled}
                  disabled={!canManageSettings}
                  onChange={handleObuFilterChange}
                  name="obuConfiguration"
                  color="primary"
                  size="small"
                />
              }
              label={l10n.getString('forms-settings-enable-obu-filters')}
              classes={{ label: classes.switch }}
            />
          </RoleGuard>
        </Box>
        {settings.storage.cam.obuFilterEnabled && obuList && (
          <ObuForm
            obuList={obuList}
            onObusChange={handleObusChange}
            canManageSettings={canManageSettings}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          />
        )}
      </Box>
    </CardContent>
  );
};

export default React.memo(SettingsLayout);
